import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Conversation } from '../types/Conversation';
import { DEFAULT_CONVERSATION_MESSAGE_PAGE_SIZE, getConversationById, markManyAsRead } from '../services/conversationService';
import { NavSection } from '../types/conversationNav';
import { idsEqual, resolveConversationMeSender } from '../utils/messageUtils';
import { applyCachedMessagesIfOmitted } from '../utils/conversationState';

interface UseSelectedConversationOptions {
  routeConversationId?: string;
  navSection: NavSection;
  accessToken?: string;
  employeeId?: string | null;
  tenantId?: string;
  resolveCachedConversation?: (conversationId: string) => Conversation | undefined;
}

interface UseSelectedConversationResult {
  selectedId: string | null;
  setSelectedId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedConv: Conversation | null;
  setSelectedConv: React.Dispatch<React.SetStateAction<Conversation | null>>;
  detailsLoading: boolean;
  detailsError: string | null;
  setDetailsError: React.Dispatch<React.SetStateAction<string | null>>;
  meSender: ReturnType<typeof resolveConversationMeSender>;
}

export const useSelectedConversation = ({
  routeConversationId,
  navSection,
  accessToken,
  employeeId,
  tenantId,
  resolveCachedConversation,
}: UseSelectedConversationOptions): UseSelectedConversationResult => {
  const { t } = useTranslation('conversations');
  const [selectedId, setSelectedId] = useState<string | null>(routeConversationId?.trim() || null);
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const lastReadMessageKeyRef = useRef<string | null>(null);
  const threadCacheRef = useRef<Map<string, Conversation>>(new Map());

  const fetchDetails = useCallback(async (conversationId: string) => {
    const cached = threadCacheRef.current.get(conversationId)
      ?? resolveCachedConversation?.(conversationId);
    if (cached?.messages.length) {
      setSelectedConv(cached);
      setDetailsLoading(false);
    } else {
      setDetailsLoading(true);
    }

    try {
      const data = await getConversationById(conversationId, {
        includeSpam: navSection === 'spam',
        messagePageSize: DEFAULT_CONVERSATION_MESSAGE_PAGE_SIZE,
        ifUnchangedSince: cached?.messages.length ? cached.updatedAt : undefined,
      });
      const next = applyCachedMessagesIfOmitted(data, cached);
      if (next?.messages.length) {
        threadCacheRef.current.set(conversationId, next);
      }
      setSelectedConv(next);
      setDetailsError(null);
    } catch {
      setDetailsError(t('failedToLoadConversationDetails'));
    } finally {
      setDetailsLoading(false);
    }
  }, [navSection, resolveCachedConversation, t]);

  useEffect(() => {
    if (selectedId != null) {
      fetchDetails(selectedId);
      return;
    }

    setSelectedConv(null);
  }, [fetchDetails, selectedId]);

  useEffect(() => {
    if (routeConversationId?.trim()) {
      setSelectedId(routeConversationId);
    }
  }, [routeConversationId]);

  useEffect(() => {
    lastReadMessageKeyRef.current = null;
  }, [selectedId]);

  useEffect(() => {
    if (selectedConv?.id && selectedConv.messages.length) {
      threadCacheRef.current.set(selectedConv.id, selectedConv);
    }
  }, [selectedConv]);

  const meSender = useMemo(() => {
    if (!selectedConv) return undefined;
    return resolveConversationMeSender(selectedConv, accessToken, tenantId, employeeId);
  }, [accessToken, employeeId, selectedConv, tenantId]);

  useEffect(() => {
    if (!selectedConv || !meSender) return undefined;

    const unreadMessages = selectedConv.messages.filter(message => {
      return !message.readBy.some(readBy => readBy.type === meSender.type && readBy.id === meSender.id);
    });

    if (unreadMessages.length === 0) {
      return undefined;
    }

    const unreadKey = `${selectedConv.id}:${unreadMessages.map(message => message.id).join(',')}`;
    if (lastReadMessageKeyRef.current === unreadKey) {
      return undefined;
    }

    let disposed = false;
    const unreadMessageIds = new Set(unreadMessages.map(message => message.id));

    const applyReadState = (conversation: Conversation): Conversation => ({
      ...conversation,
      messages: conversation.messages.map(message => {
        if (!unreadMessageIds.has(message.id)) {
          return message;
        }

        const alreadyRead = message.readBy.some(readBy => readBy.type === meSender.type && readBy.id === meSender.id);
        if (alreadyRead) {
          return message;
        }

        return {
          ...message,
          readBy: [...message.readBy, meSender],
        };
      }),
    });

    setSelectedConv(current => {
      if (!current || !idsEqual(current.id, selectedConv.id)) {
        return current;
      }

      return applyReadState(current);
    });

    const markUnreadMessages = async () => {
      try {
        await markManyAsRead(selectedConv.id, unreadMessages.map(message => message.id), meSender);
        if (!disposed) {
          lastReadMessageKeyRef.current = unreadKey;
        }
      } catch {
        if (!disposed) {
          lastReadMessageKeyRef.current = null;
        }
      }
    };

    void markUnreadMessages();

    return () => {
      disposed = true;
    };
  }, [meSender, selectedConv]);

  return {
    selectedId,
    setSelectedId,
    selectedConv,
    setSelectedConv,
    detailsLoading,
    detailsError,
    setDetailsError,
    meSender,
  };
};